export interface NavItem {
  cta?: boolean
  id: string
  label: string
}

export interface IconCard {
  icon: string
  text: string
  title: string
}

export interface Capability {
  details: Array<{
    text: string
    title: string
  }>
  image: string
  key: string
  label: string
  tags: string[]
  title: string
}

export interface PartnerLogo {
  name: string
  src: string
}

export const navItems: NavItem[] = [
  { id: 'hero', label: '首页' },
  { id: 'pain-points', label: '管理痛点' },
  { id: 'capabilities', label: '核心能力' },
  { id: 'scenarios', label: '业务场景' },
  { id: 'data', label: '数据决策' },
  { id: 'values', label: '价值优势' },
  { id: 'contact', label: '免费咨询', cta: true },
]

export const heroMetrics = [
  { value: '300+', label: '服务客户' },
  { value: '800+', label: '管理小区' },
  { value: '50万+', label: '管理房屋' },
]

export const painPoints: IconCard[] = [
  {
    title: '档案难统一',
    text: '房屋、车位、住户、设备、票据等资料集中云端沉淀，人员更迭时也能快速摸清项目家底。',
    icon: '/assets/img/pain-archives.png',
  },
  {
    title: '收费对账难',
    text: '按资产计费、自动出单、按实际用量分摊、线上缴费与电子催缴，减少人工误差和费用纠纷。',
    icon: '/assets/img/pain-billing.png',
  },
  {
    title: '任务难追踪',
    text: '工单、巡检、维保和地勤任务自动派发到人，进度实时追踪，执行结果可量化、可考核。',
    icon: '/assets/img/pain-workorders.png',
  },
  {
    title: '数据难决策',
    text: '跨区域项目统一标准、统一数据、统一监管，管理层通过数据大屏掌握经营状态和异常预警。',
    icon: '/assets/img/pain-data.png',
  },
]

export const capabilities: Capability[] = [
  {
    key: 'archives',
    label: '档案系统',
    title: '摸清家底',
    image: '/assets/img/figma/capability-archives.png',
    tags: ['资料集中', '权限可控', '交接不断档'],
    details: [
      {
        title: '资产资料',
        text: '房屋、车辆、车位、水表、电表、住户、人员、设备、票据统一沉淀，资料随项目长期留存。',
      },
      {
        title: '权限检索',
        text: '按角色授权查看和维护，降低资料泄露、误删、重复录入和个人电脑分散保存风险。',
      },
      {
        title: '顺畅交接',
        text: '物业公司变更、人员调离时，档案仍可按标准延续，帮助新团队快速接管项目。',
      },
    ],
  },
  {
    key: 'billing',
    label: '智能收费',
    title: '账务清晰可控',
    image: '/assets/img/figma/capability-billing.png',
    tags: ['自动出账', '线上缴费', '实时对账'],
    details: [
      {
        title: '自动账单',
        text: '支持物业费、水电公摊、车位费等多类费用自动生成，减少人工计算误差。',
      },
      {
        title: '聚合收银台',
        text: '业主扫码缴费、电子账单催缴、支付流水和收缴率实时汇总，财务进度更清晰可见。',
      },
      { title: '多项目规则', text: '集团统一收费规则和报表口径，降低跨项目对账、催缴和复核成本。' },
    ],
  },
  {
    key: 'workorders',
    label: '工单任务',
    title: '服务闭环可追踪',
    image: '/assets/img/figma/capability-workorders.png',
    tags: ['统一接收', '自动派单', '评价闭环'],
    details: [
      {
        title: '多入口受理',
        text: '报修、投诉、地勤任务统一进入平台，避免微信群、电话和纸质单据分散。',
      },
      {
        title: '派发到人',
        text: '任务按类型、区域、岗位分配到责任人，处理进度实时可见，异常及时提醒。',
      },
      { title: '结果评价', text: '过程留痕、结果评价、耗时统计和人员绩效关联，服务质量可量化。' },
    ],
  },
  {
    key: 'inspection',
    label: '设备巡检',
    title: '提前发现风险',
    image: '/assets/img/figma/capability-inspection.png',
    tags: ['扫码打卡', '计划巡检', '异常预警'],
    details: [
      { title: '设备台账', text: '设备资料、设备房、维保周期、负责人和巡检标准统一维护。' },
      { title: '移动巡检', text: '现场扫码打卡、拍照记录、问题上报，服务过程可追溯，减少漏检。' },
      {
        title: '风险提醒',
        text: '异常设备、逾期巡检、待处理维保任务自动提醒，降低设备停机/故障风险。',
      },
    ],
  },
  {
    key: 'iot',
    label: '智能硬件',
    title: '一网联通',
    image: '/assets/img/figma/capability-iot.png',
    tags: ['远程抄表', '智慧车场', '设备联动'],
    details: [
      {
        title: '硬件接入',
        text: '智能充电桩、远程抄表、智能监控、人脸识别、智慧车场等设备统一纳入平台。',
      },
      { title: '数据回流', text: '水电表、门禁、车场和设备状态自动回流，减少手工抄录和二次录入。' },
      { title: '流程联动', text: '硬件数据与收费、巡检、工单流程关联，异常可直接触发任务处理。' },
    ],
  },
  {
    key: 'oa',
    label: '人事 OA',
    title: '组织协同稳定',
    image: '/assets/img/figma/capability-oa.png',
    tags: ['岗位清晰', '流程可追踪', '移动协同'],
    details: [
      {
        title: '员工花名册',
        text: '员工、岗位、项目归属、角色权限统一维护，让组织数据和系统权限一致，避免权限漏洞与数据错配。',
      },
      {
        title: '薪酬与异动',
        text: '岗位异动、薪酬核算、人员调离等关键流程可记录、可追踪、可复核。',
      },
      {
        title: '移动办公',
        text: '管理层、客服、工程和外勤在移动端协同处理任务，组织架构调整不影响服务连续性。',
      },
    ],
  },
]

export const scenarios: IconCard[] = [
  {
    title: '报修处理',
    text: '线上提交、派单、处理、评价，形成闭环。',
    icon: 'i-custom-bxcl',
  },
  {
    title: '通知公告',
    text: '重要事项统一发布，触达更及时。',
    icon: 'i-custom-tzgg',
  },
  {
    title: '社区动态',
    text: '沉淀社区活动和日常服务内容。',
    icon: 'i-custom-sqdt',
  },
  {
    title: '公示公开',
    text: '费用、事务与治理信息透明展示。',
    icon: 'i-custom-gsgk',
  },
  {
    title: '投票表决',
    text: '支持社区治理事项线上参与。',
    icon: 'i-custom-tpbj',
  },
  {
    title: '问卷调查',
    text: '收集业主反馈，持续优化服务。',
    icon: 'i-custom-wjdc',
  },
  {
    title: '服务电话',
    text: '常用服务入口集中展示，减少查找成本。',
    icon: 'i-custom-fwdh',
  },
]

export const values: IconCard[] = [
  {
    title: '简单',
    text: '免安装云平台，轻松上手，降低部署和培训成本。',
    icon: 'i-custom-simple',
  },
  {
    title: '安全',
    text: '数据加密存储、权限分级管控，保障资料与资金安全。',
    icon: 'i-custom-anquan',
  },
  {
    title: '规范',
    text: '职权控制、资产计费、审核把关、流程闭环，减少人为偏差。',
    icon: 'i-custom-guifan',
  },
  {
    title: '集团化管理',
    text: '多项目统一标准、统一数据、统一监管，适合集团化物业企业。',
    icon: 'i-custom-jituan',
  },
]

export const partnerLogoRows: PartnerLogo[][] = [
  [
    { name: '智慧城管理', src: '/assets/img/partner/zhc.png' },
    { name: '三原华庭', src: '/assets/img/partner/syht.png' },
    { name: '光华景瑞', src: '/assets/img/partner/ghjr.png' },
    { name: '高经物业', src: '/assets/img/partner/gjwy.png' },
    { name: '世贸物业', src: '/assets/img/partner/smwy.png' },
    { name: '银联商务', src: '/assets/img/partner/ylsw.png' },
    { name: '第一成美物业', src: '/assets/img/partner/dycmwy.png' },
    { name: '兆南物业', src: '/assets/img/partner/znwy.png' },
  ],
  [
    { name: '衍宏物业', src: '/assets/img/partner/yhwy.png' },
    { name: '科拓股份', src: '/assets/img/partner/kegf.png' },
    { name: '海南银行', src: '/assets/img/partner/hnyh.png' },
    { name: '海康威视', src: '/assets/img/partner/hkws.png' },
    { name: '珠江物业', src: '/assets/img/partner/zjwy.png' },
    { name: '华为云', src: '/assets/img/partner/hwy.png' },
    { name: '嘉元物业', src: '/assets/img/partner/jywy.png' },
    { name: '招商银行', src: '/assets/img/partner/zsyh.png' },
  ],
]
